import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { Asset } from 'contentful'
import { debounce } from 'lodash'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { getContentfulImageUrl } from '@/molecules/ContentfulImage'

const DEFAULT_HEIGHT = 592
const DEFAULT_WIDTH = 408

const ASPECT_RATIOS = {
  MIN: 0.723,
  SM: 1,
  MD: 1.189,
  LG: 1.568,
  XL: 2,
  MAX: 2.329,
}

interface Props {
  children: React.ReactNode
  cloudinaryLandscapeImage?: string
  cloudinaryPosterImage?: string
  contentfulImage?: Asset
  layout: 'fixed' | 'responsive'
  transforms?: string
}

const CardWrapper: React.FC<Props> = ({
  children,
  cloudinaryLandscapeImage,
  cloudinaryPosterImage,
  contentfulImage,
  layout = 'fixed',
  transforms,
}) => {
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_WIDTH / DEFAULT_HEIGHT)
  const cardRef = useRef<HTMLDivElement>(null)
  const baseCardClasses = '@container h-[592px] w-full shrink-0 sm:rounded-3xl 2xl:h-[680px]'

  useEffect(() => {
    const resizeHandler = () => {
      if (!cardRef.current || layout === 'fixed') return
      const cardHeight = window.innerWidth >= 1536 ? 680 : 592
      const cardWidth = cardRef.current.clientWidth
      const ar = cardWidth / cardHeight

      if (ar < ASPECT_RATIOS.SM) {
        setAspectRatio(ASPECT_RATIOS.MIN)
      } else if (ar < ASPECT_RATIOS.MD) {
        setAspectRatio(ASPECT_RATIOS.SM)
      } else if (ar < ASPECT_RATIOS.LG) {
        setAspectRatio(ASPECT_RATIOS.MD)
      } else if (ar < ASPECT_RATIOS.XL) {
        setAspectRatio(ASPECT_RATIOS.LG)
      } else if (ar < ASPECT_RATIOS.MAX) {
        setAspectRatio(ASPECT_RATIOS.XL)
      } else {
        setAspectRatio(ASPECT_RATIOS.MAX)
      }
    }
    resizeHandler()

    const debouncedResizeHandler = debounce(resizeHandler, 200)
    window.addEventListener('resize', debouncedResizeHandler)

    return () => {
      window.removeEventListener('resize', debouncedResizeHandler)
    }
  }, [layout])

  const backgroundImageUrl =
    contentfulImage &&
    getContentfulImageUrl({
      image: contentfulImage,
      fit: 'fill',
      focus: 'face',
      height: DEFAULT_HEIGHT,
      width: Math.round(DEFAULT_HEIGHT * aspectRatio),
    })
  const backgroundStyle = contentfulImage
    ? {
        backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.80) 31.25%, rgba(0, 0, 0, 0.00) 63.6%), url("${backgroundImageUrl}")`,
      }
    : {}

  return (
    <div
      ref={cardRef}
      className={classNames(baseCardClasses, 'relative flex flex-col justify-end overflow-hidden bg-cover', {
        'sm:w-[408px] sm:first:ml-6 sm:last:mr-6 md:first:ml-8 md:last:mr-8 lg:first:ml-12 lg:last:mr-12 2xl:w-[512px] 2xl:first:ml-[4.5rem] 2xl:last:mr-[4.5rem]':
          layout === 'fixed',
      })}
      style={backgroundStyle}
    >
      {!contentfulImage && cloudinaryLandscapeImage && (
        <ResponsiveBackgroundImage
          src={aspectRatio > 1 ? cloudinaryLandscapeImage : cloudinaryPosterImage || cloudinaryLandscapeImage}
          transforms={transforms}
          responsiveAR={{
            xs: aspectRatio,
            sm: aspectRatio,
            md: aspectRatio,
            lg: aspectRatio,
            xl: aspectRatio,
            '2xl': aspectRatio,
          }}
          className="-z-10"
          preBackgroundStyles="linear-gradient(0deg, rgba(0, 0, 0, 0.80) 31.25%, rgba(0, 0, 0, 0.00) 63.6%),"
        />
      )}
      {children}
    </div>
  )
}

export default CardWrapper
